'use client';
import { createTheme, styled, Theme, CSSObject } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { Libre_Franklin } from "next/font/google";

const libreFranklin = Libre_Franklin({
  weight: ["300", "400", "500", "700"],
  subsets: ["latin"],
  display: "swap",
});

export const colors = {
  green: "#00B140",
  navy: "#0C2340",
  gray: "#97999B",
  orange: "#DB6B30",
  slate: "#4298B5",
  yellow: "#FFB600",
  pink: "#DF4661",
  paper: "#454545",
  white: "#FFFFFF",
  red: "#DF4661",
  blue: "#3a95de",
  black: "#000000",
  lavender: "#eaddff",

  success: "#00AE42",
  successHover: "#007D30",
  successDisabled: "#1d8947",
  error: "#DF4661",
  errorHover: "#DF4661",
  warningOrange: "#F06B25",
  warningOrangeHover: "#E64F00",
  warning: "#FFC942",
  warningHover: "#FFBF1F",
  textColorHover: "#7B5800",
  default: "#002340",
  defaultHover: "#002340",

  divider: "#FFFFFF26",
  slate10Per: "#4298B51A",
  black15Per: "#00000026",
  black60Per: "#00000099",
  success60Per: "#00AE4299",

  defaultBorderColor: "#00000026"
};

export const defaultStyles = {
  appPadding: "50px",
  appPaddingRight: "50px",
  appPaddingLeft: "50px",
  appPaddingTop: "30px",
  appPaddingBottom: "30px",
  appPageMinWidth: 1024,
  pagePaddingX: "50px",
  pagePadding: "0px 50px 20px 50px",
  agGridHeight: "calc(100vh - 200px)",
  footerHeight: "200px",
  modalBorderRadius: "20px",
  modalWidth: "720px",
  tooltipBorderRadius: "5px",
  tooltipMaxWidth: "400px",
  defaultAGGridClassName: "ag-theme-quartz",
  minWidth: "1024px",
};
export const borderBottomStyles = {
  borderBottomColor: colors.defaultBorderColor,
  borderBottomStyle: "solid",
  borderBottomWidth: "1px"
};

export const sideNavStyles = {
  backgroundColor: "primaryNavy.main",
  color: "common.white",
  overflowX: 'hidden',
  overflowY: 'scroll',
  scrollbarWidth: 'none', // Firefox
  transition: 'width 0.2s ease-in-out',
  '&::-webkit-scrollbar': {
    display: 'none' // Webkit browsers like Chrome, Safari
  }
};

export const sideNavWidth = 260;

const sideNavOpenedMixin = (theme: Theme): CSSObject => ({
  width: sideNavWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const sideNavClosedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
});

// Mini-variant rail Drawer used by SideNav: full width when open, icon-only
// rail when closed. Kept here (rather than styled() in the component) so the
// opened/closed mixins live alongside the rest of the shared style tokens.
export const SideNavDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: sideNavWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...sideNavOpenedMixin(theme),
          '& .MuiDrawer-paper': sideNavOpenedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...sideNavClosedMixin(theme),
          '& .MuiDrawer-paper': sideNavClosedMixin(theme),
        },
      },
    ],
  }),
);

// white, transparent hover effect. Use for navbar item hover & select (ListItemButtons)
export const sideNavRowActiveStyles = (isActive: boolean) => ({
  backgroundColor: isActive ? `${colors.white}13` : 'transparent',
  '&:hover': {
    backgroundColor: `${colors.white}1A`,
  },
});

// base style for navbar's ListItemButton 
export const navButtonBaseStyle = {
    display: 'flex',
    px: '16px',
    gap: '12px',
    color: 'white',
}

export const navButtonMainStyle = (isActive: boolean) => ({
    ...navButtonBaseStyle,
  ...sideNavRowActiveStyles(isActive),
  borderLeft: isActive ? `3px solid ${colors.green}` : '3px solid transparent',
});

export const navButtonPopoverStyle = (isActive: boolean) => ({
  ...navButtonBaseStyle,
  ...sideNavRowActiveStyles(isActive),
});

export const navButtonFooterStyle = {
  ...navButtonBaseStyle,
    borderLeft: '3px solid transparent', // footer border matches alignment, but always transparent
};

export const iconBaseStyle = {
  color: 'white',
  width: '24px',
  height: '24px',
  minWidth: '0px',
}

export const navIconStyle = (open: boolean) => ({
  ...iconBaseStyle,
  p: '0px',
  m: '0px',
});

// use to invert icon direction on boolean
export const directionalIconStyle = (open: boolean) => ({
  ...navIconStyle(open),
  transform: open ? 'scaleX(-1)' : 'scaleX(1)',
});

export const sideNavDividerStyles = {
  borderColor: colors.divider,
};

export const sideNavPopoverPaperStyles = {
  backgroundColor: 'primaryNavy.main',
  color: 'common.white',
  marginLeft: '8px',
  minWidth: '220px',
  paddingTop: '8px',
  paddingBottom: '8px',
};

// paper background for the SideNav footer's settings menu (NavMenu)
export const navMenuPaperStyle = {
  backgroundColor: 'primaryNavy.main',
  color: 'common.white',
  minWidth: `${sideNavWidth}px`,
  p: '0px',
  m: '0px'
};

export const navMenuItemStyle = {
  backgroundColor: `${colors.white}13`,
  p: '8px 16px',
  '&:hover': {
    backgroundColor: `${colors.white}1A`,
  },
}

export const sideNavHeaderTextStyles = {
  fontWeight: 600,
  fontSize: '20px',
};

export const sideNavPopoverHeaderStyles = {
  fontWeight: 500,
  fontSize: '14px',
  px: '16px',
  pb: '8px'
};

export const sideNavPopoverChildTextStyles = {
  textUnderlineOffset: 4,
  fontWeight: 400,
  fontSize: '14px',
};

export const appBreadCumbStyles = {
  display: "inline-flex",
  minHeight: "54px",
  maxHeight:"54px"
}
export const titlePageStyles={
  minHeight: '100px',
  maxHeight: '200px',
  perSectionHeight: '100px',
  zIndex : 998
}
export const appHeaderStyles = {
  minHeight: "64px",
  maxHeight: "64px",
  alignContent: "center"
}

interface AppHeaderBarProps extends MuiAppBarProps {
  open?: boolean;
}

// Sticky, top appbar used for main layout. ('open' toggles the title's visibility)
export const AppHeaderBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppHeaderBarProps>(({ theme }) => ({
  ...appHeaderStyles,
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: colors.navy,
  border: '3px solid transparent',
  padding: '8px 16px',
  display: 'flex',
  flexDirection: 'row',
  gap: '12px',
  alignItems: 'center',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

export const appHeaderLogoStyle = {
  ...iconBaseStyle,
  cursor: 'pointer',
};

export const appHeaderTitleStyle = (open: boolean) => [
  { fontWeight: 600 },
  !open && { display: 'none' },
];

declare module '@mui/material/styles' {
  interface ZIndex {
    breadcrumb: 1101 // just above the appBar (1100)
  }
}

const appTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    background: {
      paper: colors.white,
    },
  },
  typography: {
    fontFamily: libreFranklin.style.fontFamily,
  },

  components: {
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
  },
});

const theme = createTheme(appTheme, {
  palette: {
    primaryGreen: appTheme.palette.augmentColor({
      color: {
        main: colors.green,
      },
    }),
    primaryNavy: appTheme.palette.augmentColor({
      color: {
        main: colors.navy,
      },
    }),
    primaryGray: appTheme.palette.augmentColor({
      color: {
        main: colors.gray,
      },
    }),
    primaryPaper: appTheme.palette.augmentColor({
      color: {
        main: colors.paper,
      },
    }),
    secondaryOrange: appTheme.palette.augmentColor({
      color: {
        main: colors.orange,
      },
    }),
    secondarySlate: appTheme.palette.augmentColor({
      color: {
        main: colors.slate,
      },
    }),
    secondaryYellow: appTheme.palette.augmentColor({
      color: {
        main: colors.yellow,
      },
    }),
    secondaryPink: appTheme.palette.augmentColor({
      color: {
        main: colors.pink,
      },
    }),
    shadows: "none",
  },
});

export default theme;
